const CORS_PROXY_URL =
  "https://us-central1-brev-testing-1.cloudfunctions.net/cors-proxy";

export interface NotebookOutput {
  output_type: string;
  text?: string | string[];
  name?: string;
  data?: Record<string, string | string[]>;
  ename?: string;
  evalue?: string;
  traceback?: string[];
  execution_count?: number | null;
}

export interface NotebookCell {
  cell_type: string;
  source?: string | string[];
  outputs?: NotebookOutput[];
  execution_count?: number | null;
}

export interface JupyterNotebook {
  cells?: NotebookCell[];
  metadata?: {
    language_info?: { name?: string };
    kernelspec?: { language?: string };
  };
}

async function fetchRemoteText(url: string, signal?: AbortSignal) {
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch {
    throw new Error("Remote content URL is invalid");
  }
  if (!["http:", "https:"].includes(parsedUrl.protocol)) {
    throw new Error("Remote content URL must use HTTP or HTTPS");
  }

  const response = await fetch(CORS_PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url: parsedUrl.toString() }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Unable to load remote content (${response.status})`);
  }

  return response.text();
}

function getMasterBranchFallbackUrl(url: string) {
  return url.includes("/main/") ? url.replace("/main/", "/master/") : null;
}

function parseNotebook(text: string): JupyterNotebook | null {
  try {
    const value: unknown = JSON.parse(text);
    if (
      value &&
      typeof value === "object" &&
      "cells" in value &&
      Array.isArray(value.cells)
    ) {
      return value as JupyterNotebook;
    }
  } catch {
    return null;
  }

  return null;
}

export function toRawNotebookUrl(url: string): string {
  try {
    const parsed = new URL(url);

    if (parsed.hostname === "github.com") {
      const match = parsed.pathname.match(
        /^\/([^/]+)\/([^/]+)\/(?:blob|raw)\/(.+)$/,
      );
      if (match) {
        return `https://raw.githubusercontent.com/${match[1]}/${match[2]}/${match[3]}`;
      }
    }

    if (parsed.hostname.endsWith("gitlab.com") && url.includes("/-/blob/")) {
      return url.replace("/-/blob/", "/-/raw/");
    }
  } catch {
    return url;
  }

  return url;
}

export async function getRemoteMarkdown(
  markdownUrl: string,
  signal?: AbortSignal,
) {
  const markdown = await fetchRemoteText(markdownUrl, signal);
  if (markdown !== "404: Not Found") {
    return markdown;
  }

  const fallbackUrl = getMasterBranchFallbackUrl(markdownUrl);
  if (!fallbackUrl) {
    throw new Error("404: Not Found");
  }

  const fallbackMarkdown = await fetchRemoteText(fallbackUrl, signal);
  if (fallbackMarkdown === "404: Not Found") {
    throw new Error("404: Not Found");
  }

  return fallbackMarkdown;
}

export async function getRemoteNotebook(
  notebookUrl: string,
  signal?: AbortSignal,
) {
  const rawUrl = toRawNotebookUrl(notebookUrl);
  const notebook = parseNotebook(await fetchRemoteText(rawUrl, signal));
  if (notebook) {
    return notebook;
  }

  const fallbackUrl = getMasterBranchFallbackUrl(rawUrl);
  if (fallbackUrl) {
    const fallbackNotebook = parseNotebook(
      await fetchRemoteText(fallbackUrl, signal),
    );
    if (fallbackNotebook) {
      return fallbackNotebook;
    }
  }

  throw new Error("Unable to load this notebook.");
}
